import React from 'react';
import { NavLink } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import kebabCase from 'lodash/kebabCase';

import { strToLinkObj } from '../../../utils/stringUtils';
import { usePermission, useCan } from '../../../permissions';

import setupHomePageStyles from './setupHomePageStyles';

export const setupColumns = [
    {
        title: 'Finance',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'hand-holding-usd']} />,
        links: [
            { permissionId: 'setup.bank_account:view', ...strToLinkObj('Bank Accounts') },
            { permissionId: 'setup.credit_card:view', ...strToLinkObj('Credit Cards') },
            { permissionId: 'setup.general_account:view', ...strToLinkObj('General Accounts') },
            { permissionId: 'setup.income_account:view', ...strToLinkObj('Income Accounts') },
            { permissionId: 'setup.discount_account:view', ...strToLinkObj('Discount Accounts') },
            {
                permissionId: 'setup.agent_commission_account:view',
                ...strToLinkObj('Agent Commission Accounts')
            },
            { permissionId: 'setup.currency:view', ...strToLinkObj('Currencies') },
            { permissionId: 'setup.credit_company:view', ...strToLinkObj('Credit Companies') },
            {
                permissionId: 'setup.credit_payment_option:view',
                ...strToLinkObj('Credit Payment Options')
            },
            {
                permissionId: 'setup.payments_terminal:view',
                ...strToLinkObj('Payments Terminals')
            },
            { permissionId: 'setup.income_type:view', ...strToLinkObj('Income Types') },
            { permissionId: 'setup.event:view', ...strToLinkObj('Events') },
            {
                permissionId: 'setup.document_type_numbering:view',
                ...strToLinkObj('Document Types and Numbering')
            },
            {
                permissionId: 'setup.document_approval:view',
                ...strToLinkObj('General Settings')
            }
        ]
    },
    {
        title: 'Business',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'user-tie']} />,
        links: [
            { permissionId: 'setup.general_info:view', ...strToLinkObj('General Info') },
            { permissionId: 'setup.supplier_account:view', ...strToLinkObj('Supplier Accounts') },
            { permissionId: 'setup.market:view', ...strToLinkObj('Markets') },
            { permissionId: 'setup.travel_agent_type:view', ...strToLinkObj('Travel Agent Types') },
            {
                permissionId: 'setup.travel_agency_account:view',
                ...strToLinkObj('Travel Agency Accounts')
            }
        ]
    },
    {
        title: 'Services',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'concierge-bell']} />,
        links: [
            { permissionId: 'setup.service_type:view', ...strToLinkObj('Service Types') },
            {
                permissionId: 'setup.service_abbreviation:view',
                ...strToLinkObj('Service Abbreviations')
            },
            { permissionId: 'setup.service_attribute:view', ...strToLinkObj('Service Attributes') },
            { permissionId: 'setup.category:view', ...strToLinkObj('Categories') },
            { permissionId: 'setup.class:view', ...strToLinkObj('Classes') }
        ]
    },
    {
        title: 'Pricing',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'tags']} />,
        links: [
            { permissionId: 'setup.price_level:view', ...strToLinkObj('Price Levels') },
            { permissionId: 'setup.price_component:view', ...strToLinkObj('Price Components') },
            { permissionId: 'setup.component_level:view', ...strToLinkObj('Component Levels') },
            { permissionId: 'setup.component_type:view', ...strToLinkObj('Component Types') }
        ]
    },
    {
        title: 'Accommodation',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'hotel']} />,
        links: [
            { permissionId: 'setup.hotel:view', ...strToLinkObj('Hotels') },
            { permissionId: 'setup.hotel_facility:view', ...strToLinkObj('Hotel Facilities') }
        ]
    },
    {
        title: 'Travel',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'route']} />,
        links: [
            { permissionId: 'setup.guide_language:view', ...strToLinkObj('Guide Language') },
            {
                permissionId: 'setup.passenger_attribute:view',
                ...strToLinkObj('Passenger Attributes')
            },
            { permissionId: 'setup.supplier_interface:view', ...strToLinkObj('Supplier Interface') }
        ]
    },
    {
        title: 'General',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'map-marked-alt']} />,
        links: [
            { permissionId: 'setup.crm_action:view', ...strToLinkObj('Settings') },
            { permissionId: 'setup.crm_action:view', ...strToLinkObj('CRM Actions') },
            { permissionId: 'setup.crm_type:view', ...strToLinkObj('CRM Types') },
            { permissionId: 'setup.email_engagement:view', ...strToLinkObj('Email Engagements') },
            { permissionId: 'setup.email_template:view', ...strToLinkObj('Email Templates') },
            { permissionId: 'setup.country:view', ...strToLinkObj('Countries') },
            //{...strToLinkObj('Cities'), className: 'notDeveloped' },
            { permissionId: 'setup.destination:view', ...strToLinkObj('Destinations') },
            { permissionId: 'setup.location:view', ...strToLinkObj('Locations') },
            //{ ...strToLinkObj('Settings'), className: 'notDeveloped' },
            //{...strToLinkObj('Queues'), className: 'notDeveloped' },
            {
                permissionId: 'setup.travel_file_attribute:view',
                ...strToLinkObj('Travel File Attributes')
            }
        ]
    },
    {
        title: 'Admin',
        sidebarLabel: 'Users & Roles',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'user-cog']} />,
        links: [
            { permissionIdAny: ['admin.user:view', 'admin.user:edit'], ...strToLinkObj('Users') },
            { permissionIdAny: ['admin.role:view', 'admin.role:edit'], ...strToLinkObj('Roles') }
        ]
    },
    {
        title: 'Database',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'database']} />,
        links: [
            { permissionId: 'system.database:view', ...strToLinkObj('Data Sync') },
            { permissionId: 'system.database:view', ...strToLinkObj('DB Manager') },
            { permissionId: 'system.grid_settings:edit', ...strToLinkObj('Grid Settings') },
            { permissionId: 'system.upload_users:edit', ...strToLinkObj('Upload Users') },
            { permissionId: 'system.welcome_email:edit', ...strToLinkObj('Welcome Email') },
            { permissionId: 'system.app_settings:edit', ...strToLinkObj('App Settings') },
            // The route has existed since the design-token work but was never
            // listed here, so it had no sidebar entry and no Setup tile — only
            // the Database home page linked to it.
            { permissionId: 'system.appearance:edit', ...strToLinkObj('Appearance') },
            {
                permissionId: 'system.app_settings:edit',
                ...strToLinkObj('TravelBooster Retirement')
            }
        ]
    },
    {
        title: 'CRM',
        // eslint-disable-next-line react/display-name
        icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'user-cog']} />,
        links: [
            {
                permissionId: 'setup.passenger:view',
                ...strToLinkObj('Passengers')
            }
        ]
    }
];

const crmItemNames = ['CRM Actions', 'CRM Types', 'Email Engagements', 'Email Templates'];

const masterDataColumns = setupColumns
    .filter(({ title }) => title !== 'Admin' && title !== 'Database')
    .map((column) => {
        if (column.title === 'General') {
            return {
                ...column,
                title: 'Geography',
                links: column.links.filter(
                    ({ text }) => text !== 'Settings' && !crmItemNames.includes(text)
                )
            };
        }

        if (column.title === 'CRM') {
            const generalColumn = setupColumns.find(({ title }) => title === 'General');
            const crmFromGeneral =
                generalColumn?.links
                    ?.filter(({ text }) => crmItemNames.includes(text))
                    .map((link) => ({
                        ...link,
                        path: `general/${link.path}`
                    })) || [];

            return {
                ...column,
                links: [...crmFromGeneral, ...column.links]
            };
        }

        return column;
    });

const PermissionLink = ({ link, title, classes }) => {
    const { path, text, className, permissionId, permissionIdAny } = link;
    const can = useCan();
    const hasSingle = usePermission(permissionId);
    const hasAny = permissionIdAny?.length ? can.any(permissionIdAny) : false;
    const hasPermission = permissionIdAny?.length ? hasAny : hasSingle;

    if (!hasPermission) {
        return <div className={`${classes.noPermission} ${classes[className]}`}>{text}</div>;
    }

    return (
        <NavLink
            className={`${classes.linkStyle} ${classes[className]}`}
            to={`/administration/setup/${path.includes('/') ? path : `${kebabCase(title)}/${path}`}`}
            activeClassName={classes.activeRoute}
        >
            {text}
        </NavLink>
    );
};
PermissionLink.propTypes = {
    title: PropTypes.string.isRequired,
    classes: PropTypes.object.isRequired,
    link: PropTypes.shape({
        path: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        className: PropTypes.string,
        permissionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        permissionIdAny: PropTypes.arrayOf(PropTypes.string)
    }).isRequired
};
const ColumnLinks = ({ items }) => {
    const classes = setupHomePageStyles();

    return items?.map(({ title, icon: Icon, links }, idx) => (
        <Grid key={idx} container alignItems="baseline" direction="column" className={classes.wrapContent}>
            <Grid container item direction="column" style={{ width: 'unset' }}>
                <Icon className={classes.icon} />
                <Typography className={classes.categoryTitle} variant="h4">
                    {title}
                </Typography>
            </Grid>

            {links?.map((link, i) => (
                <PermissionLink key={link.text || i} link={link} title={title} classes={classes} />
            ))}
        </Grid>
    ));
};

ColumnLinks.defaultProps = {
    items: { links: { className: '' } }
};
ColumnLinks.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            className: PropTypes.string,
            icon: PropTypes.elementType.isRequired,
            links: PropTypes.arrayOf(
                PropTypes.shape({
                    path: PropTypes.string.isRequired,
                    text: PropTypes.string.isRequired,
                    className: PropTypes.string
                })
            ).isRequired
        }).isRequired
    ).isRequired
};

const SetupHomePage = () => {
    const classes = setupHomePageStyles();
    return (
        <Grid container className={classes.container}>
            <ColumnLinks items={masterDataColumns} />
        </Grid>
    );
};

export default SetupHomePage;
